import { ArrowRight, BrainCircuit, FileText, GraduationCap, Languages, Lightbulb, MessageCircle, Sparkles } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import StudentBackButton from '../components/StudentBackButton.jsx'
import { useLanguage } from '../contexts/LanguageContext.jsx'

const copy = {
  zh: {
    eyebrow: 'AI 伙伴能力',
    title: '把 AI 放在成长流程里，而不是到处找工具。',
    intro: '这里保留简历、项目、学习、写作等 AI 能力入口。真正的主线仍然是：先明确目标，再完成任务，最后沉淀成果。',
    primary: '进入 AI 伙伴',
    secondary: '回到我的成长',
    glossaryEyebrow: '怎么使用',
    glossaryTitle: '先选你现在卡住的场景',
    glossaryDesc: '不要为了“用 AI”而用 AI。先判断自己是方向不清、成果不足、简历不会写，还是学习任务拆不动。',
    groups: [
      {
        title: '简历与求职',
        icon: FileText,
        tools: [
          { name: '简历分析与优化', scene: '检查简历问题，改写项目经历，生成下一步补强建议。', audience: '准备实习、校招或转方向求职的学生', action: '优化简历', to: '/more/resume' },
          { name: '面试问题准备', scene: '根据目标岗位和项目经历，整理常见追问与回答思路。', audience: '准备面试或答辩的学生', action: '准备面试', to: '/more/interview' },
        ],
      },
      {
        title: '学习推进',
        icon: GraduationCap,
        tools: [
          { name: '学习路线', scene: '围绕当前目标查看阶段任务和学习节点。', audience: '不知道下一步学什么的人', action: '查看路线', to: '/learning' },
          { name: '课程任务拆解', scene: '把实训、课程设计或项目题目拆成今日可执行任务。', audience: '作业题目很大、不知道从哪里开始的人', action: '拆解任务', to: '/exercise' },
        ],
      },
      {
        title: '项目与成果',
        icon: BrainCircuit,
        tools: [
          { name: '成果库', scene: '查看已有项目、下一步提升建议和可写进简历的材料。', audience: '想把课程项目变成求职资产的人', action: '查看成果', to: '/projects' },
          { name: 'GitHub 项目分析', scene: '分析开源项目亮点、学习价值和简历表达角度。', audience: '计算机、软件、AI 方向学生', action: '分析 GitHub', to: '/github-analysis' },
        ],
      },
      {
        title: '表达与材料',
        icon: Languages,
        tools: [
          { name: '竞赛与证书', scene: '查看竞赛、证书和作品材料入口。', audience: '想补充经历但不知道选什么的人', action: '查看更多', to: '/more' },
          { name: 'AI 伙伴对话', scene: '把当前困惑讲清楚，让 AI 帮你转成下一步行动。', audience: '目标模糊、需要陪伴式建议的人', action: '开始对话', to: '/ai-partner' },
        ],
      },
    ],
  },
  en: {
    eyebrow: 'AI companion abilities',
    title: 'Use AI inside your growth flow, not as a pile of tools.',
    intro: 'Resume, project, learning, and writing helpers are still here. The main path is simpler: set a goal, take action, and turn work into outcomes.',
    primary: 'Open AI partner',
    secondary: 'Back to growth',
    glossaryEyebrow: 'How to choose',
    glossaryTitle: 'Start from the situation you are stuck in',
    glossaryDesc: 'Do not use AI just for the sake of using AI. First decide whether your problem is direction, outcomes, resume writing, or breaking down study tasks.',
    groups: [
      {
        title: 'Resume and career',
        icon: FileText,
        tools: [
          { name: 'Resume review', scene: 'Find resume gaps, rewrite project experience, and get improvement suggestions.', audience: 'Students preparing for internships, campus hiring, or career shifts', action: 'Improve resume', to: '/more/resume' },
          { name: 'Interview prep', scene: 'Prepare common follow-up questions and answer ideas from your role and projects.', audience: 'Students preparing for interviews or project defense', action: 'Prepare interview', to: '/more/interview' },
        ],
      },
      {
        title: 'Learning progress',
        icon: GraduationCap,
        tools: [
          { name: 'Learning route', scene: 'Review stage tasks and learning nodes around your current goal.', audience: 'Students unsure what to learn next', action: 'View route', to: '/learning' },
          { name: 'Task breakdown', scene: 'Turn course projects or training requirements into actions you can start today.', audience: 'Students facing a broad assignment and unsure where to begin', action: 'Break down task', to: '/exercise' },
        ],
      },
      {
        title: 'Projects and outcomes',
        icon: BrainCircuit,
        tools: [
          { name: 'Outcome library', scene: 'Review projects, improvement suggestions, and resume-ready materials.', audience: 'Students turning coursework into career assets', action: 'View outcomes', to: '/projects' },
          { name: 'GitHub analysis', scene: 'Understand open-source project highlights, learning value, and resume angles.', audience: 'CS, software, and AI-focused students', action: 'Analyze GitHub', to: '/github-analysis' },
        ],
      },
      {
        title: 'Writing and materials',
        icon: Languages,
        tools: [
          { name: 'Competitions and certificates', scene: 'Find entries for competitions, certificates, and portfolio materials.', audience: 'Students who want stronger experience but do not know what to choose', action: 'View more', to: '/more' },
          { name: 'AI partner chat', scene: 'Describe your confusion and turn it into a concrete next action.', audience: 'Students who need guided, companion-style advice', action: 'Start chat', to: '/ai-partner' },
        ],
      },
    ],
  },
}

function StudentAiToolsPage() {
  const navigate = useNavigate()
  const { language } = useLanguage()
  const t = copy[language] || copy.zh

  return (
    <div className="student-page student-ai-tools-page page-container">
      <StudentBackButton />
      <section className="student-hero">
        <span><Sparkles size={16} /> {t.eyebrow}</span>
        <h1>{t.title}</h1>
        <p>{t.intro}</p>
        <div className="student-hero-actions">
          <button type="button" className="primary-button" onClick={() => navigate('/ai-partner')}>
            <MessageCircle size={16} /> {t.primary}
          </button>
          <button type="button" className="secondary-button" onClick={() => navigate('/growth')}>
            {t.secondary}
          </button>
        </div>
      </section>

      <section className="student-term-glossary">
        <div>
          <span>{t.glossaryEyebrow}</span>
          <h2>{t.glossaryTitle}</h2>
          <p>{t.glossaryDesc}</p>
        </div>
        <div className="student-tool-guidance">
          <article><Lightbulb size={18} /><strong>01</strong><p>{language === 'en' ? 'Name the blocker first.' : '先说清楚卡在哪里。'}</p></article>
          <article><GraduationCap size={18} /><strong>02</strong><p>{language === 'en' ? 'Pick the matching growth step.' : '选择对应的成长环节。'}</p></article>
          <article><FileText size={18} /><strong>03</strong><p>{language === 'en' ? 'Save useful output as an outcome.' : '把有用结果沉淀成成果。'}</p></article>
        </div>
      </section>

      <section className="student-ai-tool-groups">
        {t.groups.map((group) => {
          const Icon = group.icon || Sparkles
          return (
            <article className="student-ai-tool-group" key={group.title}>
              <h2><Icon size={20} />{group.title}</h2>
              <div className="student-ai-tool-grid">
                {group.tools.map((tool) => (
                  <div className="student-ai-tool-card" key={tool.name}>
                    <h3>{tool.name}</h3>
                    <p>{tool.scene}</p>
                    <dl>
                      <dt>{language === 'en' ? 'Best for' : '适合谁'}</dt>
                      <dd>{tool.audience}</dd>
                    </dl>
                    <button
                      type="button"
                      className="primary-button"
                      onClick={() => navigate(tool.to)}
                    >
                      {tool.action}
                      <ArrowRight size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </article>
          )
        })}
      </section>
    </div>
  )
}

export default StudentAiToolsPage
